// OhmsLaw.js
import React, { useState, useCallback } from "react";
import MainCalculatorsList from "./MainCalculators";
import SharedHelmet from "../Helmet";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds,MobileUnderCalAds } from "../../Ads";
const TITLE = "Kalkulator Hukum Ohm - Hitung Tegangan, Arus, atau Resistansi";
const DESCRIPTION =
  "Kalkulator Hukum Ohm komprehensif: Hitung tegangan, arus, atau resistansi menggunakan nilai input tegangan, arus, atau resistansi. Alat yang akurat dan mudah digunakan.";
const KEYWORDS =
  "Hukum Ohm, menghitung tegangan, menghitung arus, menghitung resistansi, kalkulator Hukum Ohm";
const OG_URL = "https://do-calculate.com/calculator/id/ohmslaw";
function OhmsLawCalculator() {
  const [voltage, setVoltage] = useState("");
  const [current, setCurrent] = useState("");
  const [resistance, setResistance] = useState("");
  const [calculationMode, setCalculationMode] = useState("calculateVoltage");
  const [error, setError] = useState("");
  const [showSteps, setShowSteps] = useState(false);
  const updateCalculations = useCallback(() => {
    setError("");
    const voltageValue = parseFloat(voltage) || 0;
    const currentValue = parseFloat(current) || 0;
    const resistanceValue = parseFloat(resistance) || 0;
    switch (calculationMode) {
      case "calculateVoltage":
        if (currentValue > 0 && resistanceValue > 0) {
          const calculatedVoltage = currentValue * resistanceValue;
          setVoltage(Math.round(calculatedVoltage * 100) / 100);
        } else {
          setError("Harap masukkan arus dan resistansi dengan benar");
        }
        break;
      case "calculateCurrent":
        if (voltageValue > 0 && resistanceValue > 0) {
          const calculatedCurrent = voltageValue / resistanceValue;
          setCurrent(Math.round(calculatedCurrent * 100) / 100);
        } else {
          setError("Harap masukkan tegangan dan resistansi dengan benar");
        }
        break;
      case "calculateResistance":
        if (voltageValue > 0 && currentValue > 0) {
          const calculatedResistance = voltageValue / currentValue;
          setResistance(Math.round(calculatedResistance * 100) / 100);
        } else {
          setError("Harap masukkan tegangan dan arus dengan benar");
        }
        break;
      default:
        break;
    }
  }, [voltage, current, resistance, calculationMode]);
  const handleCalculateClick = () => {
    updateCalculations();
    setShowSteps(true);
  };
  const handleVoltageChange = (e) => setVoltage(e.target.value);
  const handleCurrentChange = (e) => setCurrent(e.target.value);
  const handleResistanceChange = (e) => setResistance(e.target.value);
  const handleModeChange = (e) => setCalculationMode(e.target.value);
  const getSolutionSteps = () => {
    switch (calculationMode) {
      case "calculateVoltage":
        return (
          <div className="alert alert-info">
            <h4>Langkah-langkah Menghitung Tegangan:</h4>
            <ol>
              <li>Masukkan arus (dalam ampere) pada kolom yang disediakan.</li>
              <li>Masukkan resistansi (dalam ohm) pada kolom yang disediakan.</li>
              <li>Tekan tombol "Hitung" untuk mendapatkan tegangan.</li>
              <li>Tegangan sama dengan arus × resistansi.</li>
            </ol>
          </div>
        );
      case "calculateCurrent":
        return (
          <div className="alert alert-info">
            <h4>Langkah-langkah Menghitung Arus:</h4>
            <ol>
              <li>Masukkan tegangan (dalam volt) pada kolom yang disediakan.</li>
              <li>Masukkan resistansi (dalam ohm) pada kolom yang disediakan.</li>
              <li>Tekan tombol "Hitung" untuk mendapatkan arus.</li>
              <li>Arus sama dengan tegangan / resistansi.</li>
            </ol>
          </div>
        );
      case "calculateResistance":
        return (
          <div className="alert alert-info">
            <h4>Langkah-langkah Menghitung Resistansi:</h4>
            <ol>
              <li>Masukkan tegangan (dalam volt) pada kolom yang disediakan.</li>
              <li>Masukkan arus (dalam ampere) pada kolom yang disediakan.</li>
              <li>Tekan tombol "Hitung" untuk mendapatkan resistansi.</li>
              <li>Resistansi sama dengan tegangan / arus.</li>
            </ol>
          </div>
        );
      default:
        return null;
    }
  };
  return (
    <div>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ height: "50px" }}
          >
            <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
          </div>
          <div className="border border-light">
            <div className="row">
              <div>
                <p>
                  Kalkulator Hukum Ohm ini memungkinkan Anda melakukan tiga jenis perhitungan
                  berdasarkan nilai input. Pilih jenis perhitungan yang sesuai dan masukkan
                  nilai yang tersedia untuk menghitung nilai yang hilang dengan akurat dan mudah.
                </p>
                <h2 style={{ fontSize: "17px" }}>
                  Menemukan Tegangan dengan Arus dan Resistansi yang Diketahui
                </h2>
                <p>
                  Jika Anda mengetahui arus (dalam ampere) dan resistansi (dalam ohm), Anda dapat
                  menghitung tegangan (dalam volt) menggunakan rumus berikut:
                </p>
                <p>
                  <b>Tegangan = Arus × Resistansi</b>
                </p>
                <h2 style={{ fontSize: "17px" }}>
                  Menemukan Arus dengan Tegangan dan Resistansi yang Diketahui
                </h2>
                <p>
                  Jika Anda mengetahui tegangan (dalam volt) dan resistansi (dalam ohm), Anda dapat
                  menghitung arus (dalam ampere) menggunakan rumus berikut:
                </p>
                <p>
                  <b>Arus = Tegangan / Resistansi</b>
                </p>
                <h2 style={{ fontSize: "17px" }}>
                  Menemukan Resistansi dengan Tegangan dan Arus yang Diketahui
                </h2>
                <p>
                  Jika Anda mengetahui tegangan (dalam volt) dan arus (dalam ampere), Anda dapat
                  menghitung resistansi (dalam ohm) menggunakan rumus berikut:
                </p>
                <p>
                  <b>Resistansi = Tegangan / Arus</b>
                </p>
                <p>
                  Gunakan alat ini untuk memasukkan nilai yang tersedia dan menghitung nilai yang hilang
                  dengan akurat, memudahkan Anda melakukan perhitungan terkait Hukum Ohm.
                </p>
                <hr />
              </div>
            </div>
            <MobileUnderCalAds />
            <div className="row">
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <div className="p-3 mb-2 bg-success text-white">
                  <h2 style={{ fontSize: "16px" }}>{TITLE}</h2>
                </div>
                <hr />
                <div >
                  <div className="form-group align-items-center">
                    <b>
                      <label
                        className="form-label ms-2"
                        htmlFor="calculationMode"
                      >
                        Pilih Jenis Perhitungan
                      </label>
                    </b>
                    <div id="calculationMode">
                      <br />
                      <div>
                        <input
                          type="radio"
                          id="calculateVoltage"
                          name="calculationMode"
                          value="calculateVoltage"
                          checked={calculationMode === "calculateVoltage"}
                          onChange={handleModeChange}
                        />
                        <label htmlFor="calculateVoltage" className="ms-2">
                          Hitung Tegangan
                        </label>
                      </div>
                      <div className="ms-3">
                        <input
                          type="radio"
                          id="calculateCurrent"
                          name="calculationMode"
                          value="calculateCurrent"
                          checked={calculationMode === "calculateCurrent"}
                          onChange={handleModeChange}
                        />
                        <label htmlFor="calculateCurrent" className="ms-2">
                          Hitung Arus
                        </label>
                      </div>
                      <div className="ms-3">
                        <input
                          type="radio"
                          id="calculateResistance"
                          name="calculationMode"
                          value="calculateResistance"
                          checked={calculationMode === "calculateResistance"}
                          onChange={handleModeChange}
                        />
                        <label htmlFor="calculateResistance" className="ms-2">
                          Hitung Resistansi
                        </label>
                      </div>
                    </div>
                  </div>
                  <br />
                  {calculationMode !== "calculateVoltage" && (
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label
                          className="form-label ms-2"
                          htmlFor="voltageInput"
                        >
                          Tegangan (Volt)
                        </label>
                      </b>
                      <input
                        id="voltageInput"
                        type="number"
                        placeholder="12"
                        value={voltage}
                        onChange={handleVoltageChange}
                        className="form-control"
                        style={{ width: "90%" }}
                      />
                    </div>
                  )}
                  {calculationMode !== "calculateCurrent" && (
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label
                          className="form-label ms-2"
                          htmlFor="currentInput"
                        >
                          Arus (Ampere)
                        </label>
                      </b>
                      <input
                        id="currentInput"
                        type="number"
                        placeholder="2"
                        value={current}
                        onChange={handleCurrentChange}
                        className="form-control"
                        style={{ width: "90%" }}
                      />
                    </div>
                  )}
                  {calculationMode !== "calculateResistance" && (
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label
                          className="form-label ms-2"
                          htmlFor="resistanceInput"
                        >
                          Resistansi (Ohm)
                        </label>
                      </b>
                      <input
                        id="resistanceInput"
                        type="number"
                        placeholder="6"
                        value={resistance}
                        onChange={handleResistanceChange}
                        className="form-control"
                        style={{ width: "90%" }}
                      />
                    </div>
                  )}
                  <br />
                  <button
                    className="btn btn-success"
                    style={{ fontSize: "20px" }}
                    onClick={handleCalculateClick}
                  >
                    Hitung
                  </button>
                  <br/>                  <div>
                    { (
                      <>
                        {error !== "" && (
                          <p className="p-3 mb-2 bg-warning text-white">
                            Kesalahan: {error}
                          </p>
                        )}
                        {calculationMode === "calculateVoltage" &&
                          voltage !== "" && (
                            <p className="p-3 mb-2 bg-success text-white">
                              Tegangan (Volt): {voltage} Volt
                            </p>
                          )}
                        {calculationMode === "calculateCurrent" &&
                          current !== "" && (
                            <p className="p-3 mb-2 bg-success text-white">
                              Arus (Ampere): {current} Ampere
                            </p>
                          )}
                        {calculationMode === "calculateResistance" &&
                          resistance !== "" && (
                            <p className="p-3 mb-2 bg-success text-white">
                              Resistansi (Ohm): {resistance} Ohm
                            </p>
                          )}
                        {showSteps && getSolutionSteps()}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
              <hr />
            </div>
          </div>
        </div>
        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList></MainCalculatorsList>
          <br />
        </div>
         </div>
</div>
  );
}
export default OhmsLawCalculator;